import React from 'react';
import { Table } from 'syngenta-digital-cropwise-react-ui-kit';
import TableActions from 'screens/Applications&WorkOrders/StepsLayout/Components/TableActions';
import FormattedNumber from 'components/FormattedNumber';
import { Maths } from 'helpers/maths';
import { getAbbreviatedUnit } from 'helpers/unitsAbbreviation';
import { PlanProductLineItem } from '../../../types';
import './ProductsTable.css';

interface ProductsTableProps {
  products: PlanProductLineItem[];
  /** Omit to render read-only (no actions column) — used by the read-only detail panel. Receives the row's `trackingId` (falling back to `productId`), not always `productId` — FR-091 allows two rows to share the same `productId`. */
  onRemove?: (key: string) => void;
  /** Re-opens FillProductDetailsDrawer for the clicked row (PR6, FR-019a). */
  onEdit?: (product: PlanProductLineItem) => void;
}

/** FR-091: `trackingId` is the real per-row identity once duplicate products are allowed — `productId` alone is no longer guaranteed unique. */
const lineItemKey = (item: PlanProductLineItem): string =>
  item.trackingId || item.productId;

const PAGE_SIZE = 20;
// Matches Applications' own ProductsTable actions column (and Plans'
// CropZonesTable) so the edit/delete icons sit flush against the table's
// right edge instead of floating in an oversized, auto-sized column.
const ACTIONS_COLUMN_WIDTH = 107;
// Matches Applications' own ProductsTable column widths for the columns
// Plans shares with it (no Rate per Tank column here).
const PRODUCT_NAME_COLUMN_WIDTH = 180;
const RATE_COLUMN_WIDTH = 144;
const TOTAL_PRODUCT_COLUMN_WIDTH = 140;
const APPLIED_AREA_COLUMN_WIDTH = 135;
const COST_COLUMN_WIDTH = 111;
// Borrowed from Applications' own hardcoded scroll value
// (`ProductsStep/index.tsx`'s TABLE_VERTICAL_PRODUCTS_SCROLL), since this
// step's header is shorter (a single button row, not a tank-info toggle row).
const TABLE_SCROLL_Y = 'calc(100vh - 358px)';

const renderFooter = (totalCost: number) => (
  total: number,
  [start, end]: [number, number]
) => (
  <div className="flex bg-white items-center justify-end">
    <div className="flex items-center text-neutral-90 text-base px-3">
      <span className="font-medium">Total Cost:</span>
      <span className="font-semibold ml-1">
        <FormattedNumber unit="usCurrency">{totalCost}</FormattedNumber>
      </span>
    </div>
    <span className="text-neutral-60 ml-4">
      {`${start} - ${end} of ${total}`}
    </span>
  </div>
);

/**
 * Read-only results table for the Products step's selected products —
 * replaces the bare `<ul>` of product names. Column set and formatting
 * (`FormattedNumber`/`getAbbreviatedUnit`, an "Appl. Acres" column combining
 * applied area with the coverage % as subtext, a "Cost" column combining
 * total cost with the per-unit price as subtext) mirrors Applications'
 * `ProductsTable` for parity, built as its own `Table` rather than reusing
 * that component directly since its tank/carrier/rate-per-tank columns don't
 * apply to Plans.
 */
const ProductsTable: React.FC<ProductsTableProps> = ({
  products,
  onRemove,
  onEdit
}) => {
  const totalCost = products.reduce(
    (sum, product) => Maths.plusRound(sum, product.totalProductCost || 0),
    0
  );

  const columns = [
    {
      title: 'Product Name',
      key: 'productName',
      width: PRODUCT_NAME_COLUMN_WIDTH,
      render: (_: unknown, product: PlanProductLineItem) => (
        <>
          <div className="text-neutral-90 font-medium truncate">
            {product.productName}
          </div>
          {product.manufacturerName && (
            <div className="text-neutral-60 text-xs truncate">
              {product.manufacturerName}
            </div>
          )}
        </>
      )
    },
    {
      title: 'Rate per Acre',
      key: 'rate',
      align: 'right' as const,
      width: RATE_COLUMN_WIDTH,
      render: (_: unknown, product: PlanProductLineItem) =>
        product.productRateValue ? (
          <>
            <FormattedNumber roundedTo={2}>
              {product.productRateValue}
            </FormattedNumber>
            <span className="ml-1">
              {`${getAbbreviatedUnit(product.productRateUnit)}/ac`}
            </span>
          </>
        ) : (
          '--'
        )
    },
    {
      title: 'Total Product',
      key: 'totalProduct',
      align: 'right' as const,
      width: TOTAL_PRODUCT_COLUMN_WIDTH,
      render: (_: unknown, product: PlanProductLineItem) =>
        product.totalProductValue ? (
          <>
            <FormattedNumber roundedTo={2}>
              {product.totalProductValue}
            </FormattedNumber>
            <span className="ml-1">
              {getAbbreviatedUnit(product.totalProductUnit)}
            </span>
          </>
        ) : (
          '--'
        )
    },
    {
      title: 'Appl. Acres',
      key: 'appliedArea',
      align: 'right' as const,
      width: APPLIED_AREA_COLUMN_WIDTH,
      render: (_: unknown, product: PlanProductLineItem) => (
        <div className="text-right">
          <FormattedNumber roundedTo={2}>{product.areaValue}</FormattedNumber>
          <span className="ml-1">ac</span>
          <div className="text-neutral-60 text-xs">
            {`${(product.coveragePercent || 0).toFixed(2)}%`}
          </div>
        </div>
      )
    },
    {
      title: 'Cost',
      key: 'cost',
      align: 'right' as const,
      width: COST_COLUMN_WIDTH,
      render: (_: unknown, product: PlanProductLineItem) =>
        product.totalProductCost ? (
          <div className="text-right">
            <FormattedNumber unit="usCurrency">
              {product.totalProductCost}
            </FormattedNumber>
            {Boolean(product.specificCostValue) && (
              <div className="text-neutral-60 text-xs">
                <FormattedNumber unit="usCurrency">
                  {product.specificCostValue}
                </FormattedNumber>
                <span>
                  {`/${getAbbreviatedUnit(product.specificCostUnit)}`}
                </span>
              </div>
            )}
          </div>
        ) : (
          '--'
        )
    },
    ...(onRemove
      ? [
          {
            title: '',
            key: 'actions',
            align: 'right' as const,
            width: ACTIONS_COLUMN_WIDTH,
            render: (_: unknown, product: PlanProductLineItem) => (
              <TableActions
                showEdit={Boolean(onEdit)}
                onEdit={onEdit ? () => onEdit(product) : undefined}
                onDelete={() => onRemove(lineItemKey(product))}
                deleteButtonLabel={`Remove ${product.productName}`}
              />
            )
          }
        ]
      : [])
  ];

  return (
    <div className="plan-products-table">
      <Table
        rowKey={lineItemKey}
        dataSource={products}
        columns={columns}
        tableLayout="fixed"
        scroll={{ y: TABLE_SCROLL_Y }}
        data-testid="products-table"
        pagination={{
          pageSize: PAGE_SIZE,
          showTotal: renderFooter(totalCost)
        }}
      />
    </div>
  );
};

export default ProductsTable;
